import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { RelayClient, RelayerTxType } from '@polymarket/builder-relayer-client'
import { BuilderConfig } from '@polymarket/builder-signing-sdk'
import { usePmSigner } from './usePmSigner'
import { usePmWalletStore } from '@/store/pmWalletStore'

const USDC_POLYGON = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'
const RELAYER_URL = 'https://relayer-v2.polymarket.com'
const CHAIN_ID = 137
const USDC_IFACE = new ethers.utils.Interface(['function transfer(address,uint256)'])

interface Intent {
  treasury: string
  amount: number
  days: number
  nonce: string
  deadline: number
  reused: boolean
}

function getBuilderSignUrl(): string {
  const env = import.meta.env.VITE_BUILDER_SIGN_URL as string | undefined
  if (env && (env.startsWith('http://') || env.startsWith('https://'))) return env
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'
  return `${base}/api/builder-sign`
}

async function fetchIntent(address: string): Promise<Intent> {
  const res = await fetch('/api/subscribe/intent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'unknown' }))
    throw new Error(err.error || `intent ${res.status}`)
  }
  return (await res.json()) as Intent
}

/**
 * Renewal flow:
 *   1) Запрашиваем intent у сервера (nonce + treasury + amount).
 *   2) Подписываем USDC.transfer(treasury, amount) клиентским PM-ключом через
 *      RelayClient (gasless meta-tx). Ключ не уходит из браузера.
 *   3) Ожидаем подтверждения relayer'а.
 *   4) Инвалидируем ['subscription'] — watcher увидит tx on-chain и проставит
 *      paid_until += days; клиент поллит и через 10-30с увидит активацию.
 */
export function useRenewSubscription() {
  const qc = useQueryClient()
  const pmWallet = usePmSigner()
  const pmProxyAddress = usePmWalletStore((s) => s.pmProxyAddress)

  return useMutation({
    mutationFn: async () => {
      if (!pmWallet?.signer) throw new Error('wallet-locked')
      if (!pmProxyAddress) throw new Error('no-proxy-address')

      const intent = await fetchIntent(pmProxyAddress.toLowerCase())

      const amountWei = ethers.BigNumber.from(Math.round(intent.amount * 1e6))
      const data = USDC_IFACE.encodeFunctionData('transfer', [intent.treasury, amountWei])

      const builderConfig = new BuilderConfig({
        remoteBuilderConfig: { url: getBuilderSignUrl() },
      })
      const client = new RelayClient(
        RELAYER_URL,
        CHAIN_ID,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        pmWallet.signer as any,
        builderConfig,
        RelayerTxType.PROXY
      )

      const response = await client.execute(
        [{ to: USDC_POLYGON, data, value: '0' }],
        'PolyStrike Visual subscription'
      )
      await response.wait()

      return { nonce: intent.nonce, amount: intent.amount, days: intent.days }
    },
    onSuccess: () => {
      if (pmProxyAddress) {
        void qc.invalidateQueries({ queryKey: ['subscription', pmProxyAddress.toLowerCase()] })
      }
    },
  })
}
